<template>
  <!-- Kanban Columns -->
  <b-skeleton-wrapper
    :loading="!board && loading"
  >
    <template #loading>
      <div class="d-flex justify-content-between">
        <div
          v-for="i in 4"
          :key="i"
          class="border ml-1 w-25"
        >
          <b-card
            v-for="j in 3"
            :key="j"
            class="border m-1"
          >
            <b-skeleton
              v-for="k in 3"
              :key="k"
              :width="`${Math.floor(k * (35 - j * i))}%`"
            />
          </b-card>
        </div>
      </div>
    </template>

    <template #default>
      <div
        v-if="board"
        class="kanban-container"
      >
        <div
          v-for="status in statuses"
          :key="status.id"
          class="kanban-column"
        >
          <div
            class="column-header"
            :style="{ borderTopColor: status.color || '#94a3b8' }"
          >
            <div class="d-flex align-items-center">
              <span
                class="status-dot mr-1"
                :style="{ backgroundColor: status.color || '#94a3b8' }"
              />
              <span class="font-weight-bold">{{ status.name }}</span>
              <b-badge
                variant="light-secondary"
                class="ml-1"
              >
                {{ status.tasks?.total || 0 }}
              </b-badge>
              <feather-icon
                v-if="status.isFinal"
                v-b-tooltip="$t('Final Status')"
                icon="CheckCircleIcon"
                size="14"
                class="ml-50 text-success"
              />
            </div>
          </div>

          <div
            class="column-tasks-overlay"
            :style="{ overflowY: 'auto' }"
            @scroll="e => fetchMoreTasks(e, status)"
          >
            <draggable
              v-if="status.tasks"
              :list="status.tasks?.data || []"
              :group="{ name: 'tasks' }"
              class="h-100 column-tasks"
              ghost-class="task-ghost"
              @change="(evt) => onTaskDrop(evt, status)"
            >
              <task-card
                v-for="task in status.tasks.data"
                :key="task.uuid"
                :task="task"
                @enter-edit-mode="$emit('enter-edit-mode', task)"
                @click="$emit('open', task)"
                @edit="$emit('edit', task)"
                @delete="$emit('delete', task)"
                @refresh="$emit('refresh')"
              />
            </draggable>
          </div>

          <div
            v-if="GET_STATUS_PAGES[status.key]?.fetchingMoreTasks"
            class="w-full text-center mt-2"
          >
            <b-spinner />
          </div>

          <b-button
            variant="flat-secondary"
            block
            size="sm"
            class="add-task-btn"
            @click="$emit('add-task', status)"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
            />
            {{ $t('Add') }}
          </b-button>
        </div>
      </div>

      <!-- No Access Alert -->
      <b-alert
        v-else-if="!loading"
        variant="danger"
        show
        class="p-2 text-center"
      >
        <div>
          <feather-icon
            icon="FrownIcon"
            size="72"
            class="mb-1"
          />
        </div>
        <h1 class="text-danger">
          {{ $t('Oops!') }}
        </h1>
        <p>
          {{ $t('You do not have access to this board or it does not exist.') }}
        </p>
      </b-alert>
    </template>
  </b-skeleton-wrapper>

</template>

<script>
import {
  BSkeletonWrapper, BSkeleton, BCard, BBadge, BButton, BAlert, BSpinner,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { mapActions, mapGetters } from 'vuex'
import { getUserData } from '@/auth/utils'
import TaskCard from '../../tasks/TaskCard.vue'

export default {
  components: {
    BSkeletonWrapper,
    BSkeleton,
    BCard,
    BBadge,
    BButton,
    BAlert,
    BSpinner,
    draggable,
    TaskCard,
  },
  props: {
    statuses: {
      type: Array,
      required: true,
    },
    onlyMe: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      me: getUserData(),
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD', 'GET_CURRENT_MILESTONE_BOARD', 'GET_FETCHING_BOARD', 'GET_LOADING', 'GET_STATUS_PAGES']),
    loading() {
      return this.GET_FETCHING_BOARD || this.GET_LOADING
    },
    board() {
      return this.GET_CURRENT_MILESTONE_BOARD ?? this.GET_CURRENT_BOARD
    },
  },
  methods: {
    ...mapActions('boards', ['moveTask', 'fetchTasksByStatusKey']),
    async onTaskDrop(evt, targetStatus) {
      if (evt.added) {
        const task = evt.added.element
        try {
          await this.moveTask({
            taskUid: task.uuid,
            currentStatusId: task.status.id,
            targetStatusId: targetStatus.id,
          })
        } catch (error) {
          this.showError(error.message)
        }
      }
    },
    async fetchMoreTasks(e, status) {
      const { scrollTop, offsetHeight, scrollHeight } = e.target
      if (scrollHeight - (scrollTop + offsetHeight) < 5 && status.tasks.hasMorePages) {
        if (!this.GET_STATUS_PAGES[status.key]?.fetchingMoreTasks) {
          const assignees = this.onlyMe ? [this.me.uuid] : this.activeFilters?.assignees || []
          const labelUids = this.activeFilters?.labels?.map(item => item.uuid) || []
          const priority = this.activeFilters?.taskPriority

          await this.fetchTasksByStatusKey({
            statusKey: status.key, assignees, labelUids, priority, search: this.taskSearch,
          })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-board.scss";
</style>
